import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { localize } from "client_legacy/ndaw/culture";
import {
	FormItem,
	TextInput,
	Form,
	FormattedText,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	IRootValidation,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import {
	SetMicrosoftServicesPasswordModel,
	setMicrosoftServicesPassword,
	checkIfCanApplyPassword
} from "client/Api/UserProfile/service";
import { SettingsPage } from "../SettingsPage";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	Action,
	ActionKind,
	buildActionsForTopBar
} from "client_legacy/ndaw/actions";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const ApplyPassword = (): ReactElement => {
	const [model, setModel] = useState<SetMicrosoftServicesPasswordModel>(defaultModel);

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const formHandler: IFormHandler<SetMicrosoftServicesPasswordModel> = useFormHandler<SetMicrosoftServicesPasswordModel>(
		model,
		setModel,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	useActiveSettingsMenuItem(SettingsPageKind.ApplyPassword);

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	useEffect(
		(): EffectWithDestructorResult => {
			async function checkIfPageIsAccessible(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					await checkIfCanApplyPassword(signal)
						.catch(handlePageDataLoadingError);

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();

			checkIfPageIsAccessible(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const productName: string = useMemo(
		() => userConfiguration().branding
			.productName,
		[]
	);

	const resetHandler = useCallback(
		(): void => {
			setModel(model);
			formHandler.validation
				.clearServerErrors();
		},
		[formHandler.validation, model]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validation: IRootValidation<SetMicrosoftServicesPasswordModel> = formHandler.validation;

			try{
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const result: boolean = await setMicrosoftServicesPassword(model, globalCancellation );
				if (result) {
					alertManager.showSuccess(localize("_ChangesSaved"));
					setModel(defaultModel);
				} else {
					alertManager.showWarning(localize("_CouldntVerifyPasswordTryAgainText"));
				}

				setIsBusy(false);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			}
		},
		[
			isBusy,
			formHandler.validation,
			globalCancellation,
			model,
			alertManager
		]
	);

	const defaultActions: Action<undefined>[] = useMemo(
		() => {
			return listDefaultActions({
				doneHandler: saveHandler,
				cancelHandler: resetHandler
			});
		},
		[resetHandler, saveHandler]
	);

	const topBarActions = useMemo(
		(): readonly Action<undefined>[] => {
			return buildActionsForTopBar<undefined, Action<undefined>>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={undefined}
			title={localize("_PasswordVerification")}
		>
			<SettingsPage.Section>
				<Form onSubmit={saveHandler} hasWidthConstrained>
					<p>
						<FormattedText
							text={localize("_PasswordVerificationText")}
							thingsToReplace={[
								{
									what: "{ProductName}",
									filler: productName
								}
							]}
						/>
					</p>
					<FormItem label={localize("_Password")}>
						<TextInput
							{...formHandler.for("password").attachToTextInput()}
							type="password"
						/>
						<ValidationError {...formHandler.for("password").attachToValidationError()} />
					</FormItem>
				</Form>
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
ApplyPassword.displayName = "ApplyPassword";

type DefaultActionsHandlers = {
	doneHandler(): void;
	cancelHandler(): void;
};

function listDefaultActions(
	handlers: DefaultActionsHandlers
): Action<undefined>[] {
	const {
		doneHandler,
		cancelHandler
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "apply",
			title: localize("_Apply"),
			canExecute: () => true,
			execute: () => doneHandler(),
			icon: "check"
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			canExecute: () => true,
			execute: () => cancelHandler(),
			icon: "xmark"
		}
	];
}

const defaultModel: SetMicrosoftServicesPasswordModel = {
	password: ""
};

const validationConfiguration: IValidationConfiguration<SetMicrosoftServicesPasswordModel> =
	createValidationConfiguration<SetMicrosoftServicesPasswordModel>();

validationConfiguration.forScalar("password")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export const applyPasswordPageRouting = {
	page: "/UserProfile/ApplyPassword"
};